import { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { getAuditTrail, exportAuditPdf } from '../services/api';
import DateRangePicker from '../components/DateRangePicker';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
  ShieldCheck,
  ArrowRight,
  Brain,
  Scales,
  CaretLeft,
  CaretRight,
  Warning,
  Buildings,
  CurrencyDollar,
  Handshake,
  Printer,
  DownloadSimple
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const scenarioMeta = {
  equipment_above_cap_threshold: { label: 'Equipment / Cap Threshold', icon: CurrencyDollar, color: 'bg-red-50 border-red-200 text-red-700' },
  owner_loan_vs_revenue: { label: 'Owner Loan vs Revenue', icon: Warning, color: 'bg-amber-50 border-amber-200 text-amber-700' },
  prepayment_vs_earned: { label: 'Prepayment vs Earned', icon: Scales, color: 'bg-violet-50 border-violet-200 text-violet-700' },
  insurance_claim_proceeds: { label: 'Insurance Proceeds', icon: ShieldCheck, color: 'bg-blue-50 border-blue-200 text-blue-700' },
  security_deposit: { label: 'Security Deposit', icon: Buildings, color: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
  intercompany_transfer: { label: 'Intercompany Transfer', icon: Handshake, color: 'bg-zinc-100 border-zinc-300 text-zinc-700' }
};

function PrintableReport({ entries, dateRange, onClose }) {
  const handlePrint = () => window.print();
  
  const scenarioCounts = {};
  let totalAmount = 0;
  entries.forEach(e => {
    (e.override_scenarios || []).forEach(s => { scenarioCounts[s] = (scenarioCounts[s] || 0) + 1; });
    totalAmount += Math.abs(e.amount || 0);
  });

  return (
    <div className="fixed inset-0 bg-white z-50 overflow-auto print:static" data-testid="print-report">
      <div className="max-w-4xl mx-auto p-8 print:p-4">
        {/* Print controls - hidden when printing */}
        <div className="flex justify-between items-center mb-8 print:hidden">
          <h1 className="text-xl font-bold">Export Preview</h1>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} className="rounded-sm">Close</Button>
            <Button onClick={handlePrint} className="bg-zinc-950 text-white rounded-sm" data-testid="print-button">
              <Printer size={16} className="mr-2" />
              Print / Save PDF
            </Button>
          </div>
        </div>

        {/* Report Header */}
        <div className="border-b-2 border-zinc-950 pb-4 mb-6">
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Override Audit Trail Report
          </h1>
          <p className="text-sm text-zinc-500 mt-1" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
            Generated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </p>
          {(dateRange.dateFrom || dateRange.dateTo) && (
            <p className="text-sm text-zinc-500" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
              Period: {dateRange.dateFrom || 'Start'} to {dateRange.dateTo || 'Present'}
            </p>
          )}
        </div>

        {/* Summary */}
        <div className="grid grid-cols-3 gap-6 mb-8 border border-zinc-200 p-6">
          <div>
            <p className="text-xs uppercase tracking-widest text-zinc-500">Total Overrides</p>
            <p className="text-3xl font-bold font-mono">{entries.length}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-zinc-500">Total Amount Affected</p>
            <p className="text-3xl font-bold font-mono">${totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-zinc-500">Override Types</p>
            <div className="mt-1 space-y-1">
              {Object.entries(scenarioCounts).map(([s, c]) => (
                <p key={s} className="text-sm">{s.replace(/_/g, ' ')}: <span className="font-mono font-bold">{c}</span></p>
              ))}
            </div>
          </div>
        </div>

        {/* Detail entries */}
        <h2 className="text-lg font-bold mb-4 border-b border-zinc-200 pb-2">Override Details</h2>
        {entries.map((entry, i) => (
          <div key={entry.audit_id} className="mb-6 border border-zinc-200 p-4 break-inside-avoid">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <span className="font-mono text-sm text-zinc-500">#{i + 1}</span>
                <span className="px-2 py-0.5 text-xs uppercase tracking-wider border rounded-sm bg-zinc-100">
                  {(entry.override_scenarios || []).map(s => s.replace(/_/g, ' ')).join(', ')}
                </span>
              </div>
              <span className="text-xs text-zinc-500 font-mono">{entry.date}</span>
            </div>
            
            <div className="grid grid-cols-4 gap-4 mb-3 text-sm">
              <div><span className="text-zinc-500">Vendor:</span> <strong>{entry.vendor_name}</strong></div>
              <div><span className="text-zinc-500">Amount:</span> <strong className="font-mono">${Math.abs(entry.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong></div>
              <div><span className="text-zinc-500">Pattern:</span> <span className="font-mono">{entry.pattern_confidence}%</span></div>
              <div><span className="text-zinc-500">Decision:</span> <strong className="uppercase">{(entry.final_decision || '').replace('_', ' ')}</strong></div>
            </div>

            <div className="bg-zinc-50 p-3 space-y-1 text-sm border border-zinc-100">
              <p><span className="text-violet-600 font-medium">Pattern:</span> {entry.pattern_summary}</p>
              <p><span className="text-blue-600 font-medium">Policy:</span> {entry.policy_check}</p>
              <p><span className="text-amber-600 font-medium">Review:</span> {entry.review_reason_display}</p>
            </div>

            {entry.context_overrides?.length > 0 && (
              <div className="mt-2 text-xs text-zinc-600">
                <strong>Override Detail:</strong> {entry.context_overrides[0].reason}
              </div>
            )}
            <p className="text-xs text-zinc-400 mt-2 font-mono">Ref: {entry.transaction_id}</p>
          </div>
        ))}

        {/* Footer */}
        <div className="mt-8 pt-4 border-t-2 border-zinc-950 text-center text-xs text-zinc-500">
          <p>AI Snugglemind - Override Audit Trail Report - {entries.length} entries</p>
          <p>This report documents all instances where business context rules overrode AI pattern recognition.</p>
        </div>
      </div>
    </div>
  );
}

export default function AuditTrailPage() {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [showReport, setShowReport] = useState(false);
  const limit = 20;

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit, skip: page * limit };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const response = await getAuditTrail(params);
      setEntries(response.data.entries);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to load audit trail');
    } finally {
      setLoading(false);
    }
  }, [page, dateFrom, dateTo]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const totalPages = Math.ceil(total / limit);

  if (showReport) {
    return <PrintableReport entries={entries} dateRange={{ dateFrom, dateTo }} onClose={() => setShowReport(false)} />;
  }

  if (loading) {
    return (
      <Layout title="Audit Trail">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-zinc-950 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Audit Trail">
      <div data-testid="audit-trail-page" className="space-y-6">
        {/* Header */}
        <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-zinc-200 border border-zinc-300 flex items-center justify-center rounded-sm">
              <ShieldCheck size={24} weight="duotone" className="text-zinc-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold tracking-tight mb-1">Override Audit Log</h2>
              <p className="text-sm text-zinc-600">
                Every time business context overrides AI pattern recognition, it's logged here for compliance.
              </p>
              <p className="text-xs text-zinc-500 mt-1">
                Total overrides: <span className="font-mono font-semibold">{total}</span>
              </p>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-between">
          <DateRangePicker
            dateFrom={dateFrom}
            dateTo={dateTo}
            onChange={({ dateFrom: df, dateTo: dt }) => { setDateFrom(df); setDateTo(dt); setPage(0); }}
          />
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  const params = {};
                  if (dateFrom) params.date_from = dateFrom;
                  if (dateTo) params.date_to = dateTo;
                  const response = await exportAuditPdf(params);
                  const url = window.URL.createObjectURL(new Blob([response.data]));
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `audit_trail_report_${new Date().toISOString().slice(0,10)}.pdf`;
                  a.click();
                  window.URL.revokeObjectURL(url);
                  toast.success('PDF downloaded');
                } catch (e) {
                  toast.error('Failed to generate PDF');
                }
              }}
              disabled={entries.length === 0}
              className="rounded-sm"
              data-testid="export-pdf-button"
            >
              <DownloadSimple size={16} className="mr-2" />
              Download PDF
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowReport(true)}
              disabled={entries.length === 0}
              className="rounded-sm"
              data-testid="export-full-button"
            >
              <Printer size={16} className="mr-2" />
              Print Report
            </Button>
          </div>
        </div>

        {/* Entries */}
        {entries.length === 0 ? (
          <div className="bg-white border border-zinc-200 rounded-sm p-12 text-center">
            <ShieldCheck size={48} className="mx-auto mb-4 text-zinc-300" />
            <p className="font-medium mb-2">No overrides found</p>
            <p className="text-sm text-zinc-500">
              {dateFrom || dateTo ? 'Try adjusting the date range.' : 'Overrides are logged when context rules override AI pattern recognition.'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {entries.map((entry) => {
              const scenarios = entry.override_scenarios || [];
              const firstScenario = scenarios[0] || '';
              const meta = scenarioMeta[firstScenario] || { label: firstScenario.replace(/_/g, ' '), icon: ShieldCheck, color: 'bg-zinc-100 border-zinc-300 text-zinc-700' };
              const Icon = meta.icon;

              return (
                <div key={entry.audit_id} data-testid={`audit-entry-${entry.audit_id}`} className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
                  <div className="px-6 py-3 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`px-3 py-1 text-xs uppercase tracking-widest border rounded-sm flex items-center gap-1.5 ${meta.color}`}>
                        <Icon size={14} weight="duotone" />
                        {meta.label}
                      </div>
                      <span className="text-xs text-zinc-400 font-mono">{entry.date}</span>
                      {entry.client_name && <span className="text-xs text-zinc-500">Client: <span className="font-medium">{entry.client_name}</span></span>}
                    </div>
                    <span className="text-xs text-zinc-400">
                      {new Date(entry.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-6 mb-4">
                      <div>
                        <p className="text-xs text-zinc-500">Vendor</p>
                        <p className="font-medium text-sm">{entry.vendor_name}</p>
                      </div>
                      <div>
                        <p className="text-xs text-zinc-500">Amount</p>
                        <p className="font-mono font-medium text-sm">${Math.abs(entry.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                      </div>
                      <div>
                        <p className="text-xs text-zinc-500">Pattern</p>
                        <p className="font-mono text-sm">{entry.pattern_confidence}%</p>
                      </div>
                      <ArrowRight size={16} className="text-zinc-300" />
                      <div>
                        <p className="text-xs text-zinc-500">Decision</p>
                        <span className={`px-2 py-0.5 text-xs uppercase border rounded-sm ${entry.final_decision === 'auto_post' ? 'bg-emerald-50 text-emerald-700 border-emerald-500' : 'bg-amber-50 text-amber-700 border-amber-500'}`}>
                          {(entry.final_decision || 'unknown').replace('_', ' ')}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-2 bg-zinc-50 border border-zinc-100 p-4 rounded-sm">
                      <div className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 bg-violet-500 rounded-full mt-1.5 flex-shrink-0"></span>
                        <p className="text-xs text-zinc-700">{entry.pattern_summary}</p>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></span>
                        <p className="text-xs text-zinc-700">{entry.policy_check}</p>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mt-1.5 flex-shrink-0"></span>
                        <p className="text-xs text-zinc-700">{entry.review_reason_display}</p>
                      </div>
                    </div>
                    {entry.context_overrides?.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-zinc-100">
                        <p className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Override Detail</p>
                        {entry.context_overrides.map((o, i) => <p key={`override-${i}-${(o.reason || '').slice(0, 16)}`} className="text-xs text-zinc-600">{o.reason}</p>)}
                      </div>
                    )}
                    <p className="text-xs text-zinc-400 mt-3 font-mono">Transaction: {entry.transaction_id}</p>
                  </div>
                </div>
              );
            })}

            {totalPages > 1 && (
              <div className="flex items-center justify-between pt-4">
                <p className="text-sm text-zinc-500">Showing {page * limit + 1} to {Math.min((page + 1) * limit, total)} of {total}</p>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className="rounded-sm"><CaretLeft size={16} /></Button>
                  <span className="text-sm text-zinc-600 px-2">Page {page + 1} of {totalPages}</span>
                  <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1} className="rounded-sm"><CaretRight size={16} /></Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
